import { identity } from './function';

// Create a comparator over T with the transformation f
// E.g., for string s, toComparator(s => s.length)('hey', 'heyo') ==> -1
function toComparator<T, U extends string | number | Date>(
  f: (arg0: T) => U,
): (arg0: T, arg1: T) => number {
  return (a, b) => {
    const fa = f(a);
    const fb = f(b);

    if (fa < fb) {
      return -1;
    }
    if (fa > fb) {
      return 1;
    }
    return 0;
  };
}

// Sort an array based on optional comparator function `fn` without modifying
// original array.
// If `fn` not provided, uses "natural" sort (as in Array.prototype.sort)
export function sort<T>(
  arr: readonly T[],
  fn?: (arg0: T, arg1: T) => number,
): T[] {
  return arr.slice().sort(fn);
}

// Returns a new array sorted by the iteratee function
// E.g., sortBy(['ab', 'a', 'abc'], s => s.length) ==> ['a', 'ab', 'abc']
export function sortBy<T>(
  a: readonly T[],
  iteratee: (arg0: T) => string | number | Date,
  reverse = false,
): T[] {
  const l = a.slice().sort(toComparator(iteratee));
  return reverse ? l.reverse() : l;
}

// Sum an array of numbers
export const sum = (x: number[]) => x.reduce((acc, val) => acc + val, 0);

// Returns all items in `a` that are not in `b`.
// Optional transformation tx over which to check equality
// difference([{id: 1, v: 2}, {id: 2, v: 3}], [{id: 1, v: 4}], x => x.id)
// => [{id: 2, v: 3}]
export function difference<T>(
  a: T[],
  b: T[],
  tx: (arg0: T) => unknown = identity,
): T[] {
  return a.filter((i) => !b.map(tx).includes(tx(i)));
}

export function intersection<T>(
  a: T[],
  b: T[],
  tx: (arg0: T) => unknown = identity,
): T[] {
  return a.filter((i) => b.map(tx).includes(tx(i)));
}

export function setEquals<T>(a: T[], b: T[]) {
  return a.length === b.length && a.every((i) => b.includes(i));
}

export function removeArrayIndex<T>(items: T[], index: number): T[] {
  return items.slice(0, index).concat(items.slice(index + 1));
}

/**
 * @deprecated Use instead `const newArray = [...items]; newArray[index] = newValue`
 */
export function updateArrayItem<T>(a: T[], index: number, newValue: T): T[] {
  return a
    .slice(0, index)
    .concat([newValue])
    .concat(a.slice(index + 1));
}

export function shuffleArray<T>(originalArr: readonly T[]): T[] {
  const arr = [...originalArr];
  let currentIndex = arr.length;
  while (currentIndex !== 0) {
    const randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex -= 1;
    [arr[currentIndex], arr[randomIndex]] = [
      arr[randomIndex],
      arr[currentIndex],
    ];
  }
  return arr;
}
